import type { ProductRelation } from '../../../core/api/product-master/models/product-selection.model';
import type { AddressFormValue } from '../../ship-from-ship-to/models/address-form.model';
import {
  SIGNATURE_REQUIREMENT_NONE,
  SIGNATURE_REQUIREMENT_RES,
  SIGNATURE_REQUIREMENT_ZS50,
  type SignatureRequirement,
} from './signature-protection.form';

const SIGNATURE_RELATION_CODES = {
  osnr: 'OSNR',
  residential: 'RES',
  adult: 'ZS50',
} as const;

/** Result of applying Product Master relations and destination type to the signature select. */
export interface ResolvedSignatureRequirement {
  options: SignatureRequirement[];
  requirement: SignatureRequirement;
}

/**
 * Reads Product Master relation rows used to decide signature dropdown availability.
 */
export function readProductRelations(relations: unknown): ProductRelation[] {
  if (!Array.isArray(relations)) {
    return [];
  }

  return relations.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const record = entry as { relationshipType?: unknown; productCode?: unknown };
    if (typeof record.productCode !== 'string' || record.productCode.trim().length === 0) {
      return [];
    }

    return [
      {
        relationshipType:
          typeof record.relationshipType === 'string' ? record.relationshipType : '',
        productCode: record.productCode.trim().toUpperCase(),
      },
    ];
  });
}

/**
 * Builds the allowed signature codes for a courier product from Product Master relations.
 * Only OSNR, RES, and ZS50 are user-selectable in Phase 1.
 */
export function availableSignatureRequirements(
  relations: ProductRelation[] | undefined,
): SignatureRequirement[] {
  const codes = new Set(readProductRelations(relations).map((relation) => relation.productCode));
  const options: SignatureRequirement[] = [];

  if (codes.has(SIGNATURE_RELATION_CODES.osnr)) {
    options.push(SIGNATURE_REQUIREMENT_NONE);
  }

  if (codes.has(SIGNATURE_RELATION_CODES.residential)) {
    options.push(SIGNATURE_REQUIREMENT_RES);
  }

  if (codes.has(SIGNATURE_RELATION_CODES.adult)) {
    options.push(SIGNATURE_REQUIREMENT_ZS50);
  }

  return options;
}

/**
 * Returns offered signature codes whose Product Master relation is Inclusive.
 * Evening Signature Required uses Inclusive RES so the dropdown can show "(Included)".
 */
export function inclusiveSignatureRequirements(relations: unknown): SignatureRequirement[] {
  const includedCodes = new Set(
    readProductRelations(relations)
      .filter((relation) => relation.relationshipType.toLowerCase() === 'inclusive')
      .map((relation) => relation.productCode),
  );
  const options: SignatureRequirement[] = [];

  if (includedCodes.has(SIGNATURE_RELATION_CODES.residential)) {
    options.push(SIGNATURE_REQUIREMENT_RES);
  }

  if (includedCodes.has(SIGNATURE_RELATION_CODES.adult)) {
    options.push(SIGNATURE_REQUIREMENT_ZS50);
  }

  return options;
}

/**
 * Chooses the OM-347 default: OSNR for residential destinations when offered, otherwise RES, then ASR.
 */
export function defaultSignatureRequirement(
  available: SignatureRequirement[],
  destinationResidential: boolean,
): SignatureRequirement {
  if (destinationResidential && available.includes(SIGNATURE_REQUIREMENT_NONE)) {
    return SIGNATURE_REQUIREMENT_NONE;
  }

  if (available.includes(SIGNATURE_REQUIREMENT_RES)) {
    return SIGNATURE_REQUIREMENT_RES;
  }

  if (available.includes(SIGNATURE_REQUIREMENT_ZS50)) {
    return SIGNATURE_REQUIREMENT_ZS50;
  }

  return available[0] ?? SIGNATURE_REQUIREMENT_NONE;
}

/**
 * Resolves signature dropdown options and the value to apply after a product or destination change.
 * Keeps the current selection when it remains available; otherwise snaps to the new default.
 */
export function resolveSignatureRequirement(input: {
  relations?: unknown;
  destinationResidential: boolean;
  current?: SignatureRequirement | null;
}): ResolvedSignatureRequirement {
  const options = availableSignatureRequirements(readProductRelations(input.relations));
  const current = input.current;

  if (current && options.includes(current)) {
    return { options, requirement: current };
  }

  return {
    options,
    requirement: defaultSignatureRequirement(options, input.destinationResidential),
  };
}

/**
 * Destination residential for signature defaults: service-ability first, then ship-to form.
 */
export function resolveDestinationResidential(flow: {
  destinationResidential?: boolean;
  addresses?: { shipTo?: Pick<AddressFormValue, 'residential'> | { residential?: boolean } };
}): boolean {
  if (typeof flow.destinationResidential === 'boolean') {
    return flow.destinationResidential;
  }

  return flow.addresses?.shipTo?.residential === true;
}
